import { TimeRange } from "@dataseat-dsp-client/lib-reporter";
import { getIsoDateNow } from "@dataseat-dsp-client/lib-reporter/build/helpers/dateISO";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import Popper from "@mui/material/Popper";
import DateRangeInputs from "components-redesign/DatetimeInput/DateRangeInputs";
import PresetsTrigger from "components-redesign/DatetimeInput/PresetsTrigger";
import RangeDatePicker from "components-redesign/DatetimeInput/RangeDatePicker";
import { Paper, makeStyles } from "components/mui-core";
import { endOfIsoDay } from "helpers";
import { ISOstring } from "model/modelTypes";
import {
  CSSProperties,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import PresetsSidebar from "./PresetsSidebar";
import { IRangePreset } from "./presetsOptions";
import { getPreviousPresetUtc } from "./utils";

const useStyles = makeStyles()({
  datePickerWithSidebar: {
    display: "flex",
    backgroundColor: "#fff",
    borderRadius: "16px",
    overflow: "hidden",
  },
  popper: {
    zIndex: 9999,
  },
  datePickerContent: {
    borderRadius: "16px !important",
  },
  rangePicker: {
    boxShadow: "-5px 2px 13.6px 1px rgba(0, 0, 0, 0.07)",
  },
  disabled: {
    opacity: 0.5,
    pointerEvents: "none",
  },
});

export interface DatetimeRangeProps {
  value: [ISOstring | null, ISOstring | null];
  onChange: ([startTime, endTime]: [
    ISOstring | null,
    ISOstring | null,
  ]) => void;
  onClose?: () => void;
  label?: string;
  valueLabelMask?: string;
  placeholder?: string;
  error?: string;
  helperText?: string;
  hideTimePicker?: boolean;
  minDate?: string;
  maxDate?: string;
  presets?: TimeRange[];
  renderSidebar?: () => React.ReactNode;
  disableStartDate?: boolean;
  disableEndDate?: boolean;
  disabled?: boolean;
  disableFuture?: boolean;
  style?: CSSProperties;
}

const DatetimeRange = ({
  value,
  onChange,
  onClose,
  label,
  valueLabelMask = "dd/mm/yy",
  placeholder = "DD/MM/YYYY",
  error,
  helperText,
  hideTimePicker = false,
  minDate,
  maxDate,
  presets,
  renderSidebar,
  disableStartDate = false,
  disableEndDate = false,
  disabled = false,
  disableFuture = false,
  style,
}: DatetimeRangeProps) => {
  const { classes: styles } = useStyles();
  const showTime = !hideTimePicker;
  const hasPresets = !!presets?.length;
  const hasSidebar = hasPresets || !!renderSidebar;

  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [dateRange, setDateRange] =
    useState<[ISOstring | null, ISOstring | null]>(value);
  const [selectedRange, setSelectedRange] =
    useState<[ISOstring | null, ISOstring | null]>(value);
  const anchorEl = useRef(null);
  const inputsFocused = useRef(false);

  const [startTime, endTime] = value;

  useEffect(() => {
    setDateRange(value);
    setSelectedRange(value);
  }, [value]);

  const computedMaxDate = useMemo(() => {
    if (maxDate) return maxDate;
    if (disableFuture) return getIsoDateNow();
    return undefined;
  }, [maxDate, disableFuture]);

  const quickLinkPreset = useMemo(() => {
    if (!hasPresets) return null;
    return getPreviousPresetUtc(startTime, endTime);
  }, [startTime, endTime, hasPresets]);

  const displayLabel = useMemo(
    () => quickLinkPreset?.label ?? null,
    [quickLinkPreset]
  );

  const handleQuickSelection = useCallback(
    (option: IRangePreset) => {
      setDateRange(option.value);
      onChange([option.value[0], option.value[1]]);
      setSelectedRange(option.value);
      setDatePickerOpen(false);
    },
    [onChange]
  );

  const handleOpen = useCallback(() => {
    if (!disabled) setDatePickerOpen(true);
  }, [disabled]);

  const handleClose = useCallback(() => {
    if (!hasPresets && inputsFocused.current) return;
    setDatePickerOpen(false);
    setSelectedRange(dateRange);
    onClose?.();
  }, [onClose, dateRange, hasPresets]);

  const handleDateSubmit = useCallback(
    (newDateRange: [ISOstring, ISOstring]) => {
      const [newStart, newEnd] = newDateRange;
      // With time on, RangeDatePicker already merged the chosen time (defaulting
      // to 00:00 start / 23:59:59 end). Otherwise force the end to end-of-day.
      const result: [ISOstring, ISOstring] = showTime
        ? newDateRange
        : [newStart, endOfIsoDay(newEnd)];
      setDateRange(result);
      setSelectedRange(result);
      setDatePickerOpen(false);
      onChange(result);
    },
    [onChange, showTime]
  );

  const handleInputFocus = useCallback(() => {
    inputsFocused.current = true;
    handleOpen();
  }, [handleOpen]);

  const handleInputBlur = useCallback(() => {
    inputsFocused.current = false;
  }, []);

  const popoverContent = (
    <div className={hasSidebar ? styles.datePickerWithSidebar : undefined}>
      {hasPresets && (
        <PresetsSidebar
          visiblePresets={presets}
          startDate={startTime}
          endDate={endTime}
          onOptionSelect={handleQuickSelection}
        />
      )}
      {!hasPresets && renderSidebar && renderSidebar()}
      <RangeDatePicker
        inline
        value={selectedRange}
        onSubmit={handleDateSubmit}
        onSelect={setSelectedRange}
        onCancel={handleClose}
        initialValue={dateRange}
        minDate={minDate}
        maxDate={computedMaxDate}
        className={styles.rangePicker}
        disableStartDate={disableStartDate}
        disableEndDate={disableEndDate}
        disabled={disabled}
        hideInputs={!hasSidebar}
        showTime={showTime}
      />
    </div>
  );

  return (
    <div className={disabled ? styles.disabled : undefined}>
      {hasPresets ? (
        <PresetsTrigger
          ref={anchorEl}
          label={label}
          displayLabel={displayLabel}
          startTime={startTime}
          endTime={endTime}
          valueLabelMask={valueLabelMask}
          placeholder={placeholder}
          showTime={showTime}
          error={error}
          helperText={helperText}
          disabled={disabled}
          onClick={handleOpen}
        />
      ) : (
        <DateRangeInputs
          ref={anchorEl}
          value={value}
          onChange={onChange}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          label={label}
          placeholder={placeholder}
          showTime={showTime}
          error={error}
          helperText={helperText}
          disabled={disabled}
          disableStartDate={disableStartDate}
          disableEndDate={disableEndDate}
          minDate={minDate}
          maxDate={computedMaxDate}
        />
      )}
      <Popper
        open={datePickerOpen}
        anchorEl={anchorEl.current}
        placement="bottom-start"
        style={style}
        className={styles.popper}>
        <ClickAwayListener onClickAway={handleClose}>
          <Paper
            elevation={3}
            className={styles.datePickerContent}>
            {popoverContent}
          </Paper>
        </ClickAwayListener>
      </Popper>
    </div>
  );
};

export default DatetimeRange;
